import React, { useEffect, useState, useCallback } from "react";
import { Banknote, Sparkles, CheckCircle2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/apiClient";

const CHF_FMT = new Intl.NumberFormat("de-CH", {
  style: "currency",
  currency: "CHF",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (amount, currency = "CHF") => {
  if (currency === "CHF") return CHF_FMT.format(amount || 0);
  try {
    return new Intl.NumberFormat("de-CH", {
      style: "currency", currency, minimumFractionDigits: 2,
    }).format(amount || 0);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

export function AutoBookedTodayCard({ companyId, days = 1 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(
        `/companies/${companyId}/dashboard/auto-booked-today?days=${days}`,
      );
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, days]);

  useEffect(() => { load(); }, [load]);

  // Render nichts, wenn keine Auto-Buchungen vorliegen (Card nur sichtbar, wenn relevant)
  if (!loading && (!data || data.count === 0)) return null;

  return (
    <Card
      className="p-5 bg-gradient-to-br from-sky-50 via-white to-emerald-50 border-sky-200"
      data-testid="auto-booked-today-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-sky-100 border border-sky-200 flex items-center justify-center">
            <Banknote className="h-5 w-5 text-sky-700" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-sky-700/80 font-medium">
              {days === 1 ? "Heute" : `Letzte ${days} Tage`} via PostFinance
            </div>
            <div className="text-lg font-semibold text-slate-900">
              Automatisch verbucht
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="text-slate-500"
          data-testid="auto-booked-today-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Anzahl</div>
          <div className="text-2xl font-bold text-slate-900" data-testid="auto-booked-today-count">
            {data?.count ?? 0}
          </div>
          <div className="text-[11px] text-slate-500">Rechnung(en)</div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-0.5">Summe</div>
          <div className="text-2xl font-bold text-emerald-700" data-testid="auto-booked-today-total">
            {formatCurrency(data?.total_amount ?? 0, data?.currency || "CHF")}
          </div>
          <div className="text-[11px] text-slate-500">automatisch settled</div>
        </div>
        {(data?.ai_matched_count ?? 0) > 0 && (
          <div>
            <div className="text-xs text-slate-500 mb-0.5 flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-violet-600" />
              KI-Match
            </div>
            <div className="text-2xl font-bold text-violet-700" data-testid="auto-booked-today-ai">
              {data.ai_matched_count}
            </div>
            <div className="text-[11px] text-slate-500">via Confidence ≥ 85%</div>
          </div>
        )}
      </div>

      {(data?.items?.length ?? 0) > 0 && (
        <div className="mt-4 border-t border-sky-100 pt-3">
          <div className="text-xs font-medium text-slate-600 mb-2">Zuletzt gebucht</div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {(data.items || []).slice(0, 5).map((it) => (
              <div
                key={it.invoice_id}
                className="flex items-center justify-between text-sm py-1"
                data-testid={`auto-booked-row-${it.invoice_id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                  <span className="font-medium text-slate-900 truncate">{it.invoice_number}</span>
                  {it.customer_name && (
                    <span className="text-slate-500 truncate">· {it.customer_name}</span>
                  )}
                  {it.ai_confidence != null && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] bg-violet-50 text-violet-700 border-violet-200"
                      title={it.ai_reason || ""}
                    >
                      <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                      KI {(it.ai_confidence * 100).toFixed(0)}%
                    </Badge>
                  )}
                </div>
                <span className="text-slate-700 font-medium flex-shrink-0 ml-2">
                  {formatCurrency(it.amount, it.currency)}
                </span>
              </div>
            ))}
            {data.items.length > 5 && (
              <div className="text-[11px] text-slate-500 italic pt-1">
                … und {data.items.length - 5} weitere
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
